import { fromPairs, isEmpty, memoize } from 'lodash-es';
import { House, Play } from 'phosphor-react';
import type { FormEvent } from 'react';
import { useId, useMemo, useState } from 'react';
import type { OptionsProvider } from '../lib/ds';
import { ButtonCtaPrimary, InputDate, InputText, Label, Select, Textarea } from '../lib/ds';
import { Header3 } from '../lib/ds/typography/typography';
import { LoadingSpinner } from '../lib/loading-spinner';

export type IndividuelleAufgabeObjekt = {
  id: string;
  nummer: string;
  name: string;
};

export type IndividuelleAufgabeFormValues = {
  objektId: string;
  titel: string;
  beschreibung: string;
  kategorie: string;
  typ: string;
  faelligkeit?: string;
};

export type IndividuelleAufgabeFormProps = {
  objekte: IndividuelleAufgabeObjekt[];
  onSubmit: (values: IndividuelleAufgabeFormValues) => void;
  submitting: boolean;
};

type FormState = {
  objektId?: string;
  titel?: string;
  faelligkeit?: string;
  beschreibung?: string;
};

const MIN_CHARS_TO_SEARCH = 3;

const isValid = (state: FormState): boolean => !isEmpty(state.objektId) && !isEmpty(state.titel) && !isEmpty(state.beschreibung);

export const IndividuelleAufgabeForm = ({ objekte, onSubmit, submitting }: IndividuelleAufgabeFormProps) => {
  const formId = useId();
  const [formValues, setFormValues] = useState<FormState>({});

  const canSubmit = isValid(formValues) && !submitting;

  const objekteProvider: OptionsProvider = useMemo(() => memoize(
    (suchbegriff: string) => {
      if (suchbegriff.length < MIN_CHARS_TO_SEARCH) {
        return [];
      }

      return fromPairs(objekte
        .filter(({ nummer, name }) => nummer.includes(suchbegriff.toLocaleLowerCase()) || name.toLocaleLowerCase().includes(suchbegriff.toLocaleLowerCase()))
        .map(({ id, nummer, name }) => [id as string, `${nummer}-${name}`]));
    },
  ), [objekte]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isValid(formValues) && formValues.objektId) {
      onSubmit({
        objektId: formValues.objektId,
        titel: formValues.titel ?? '',
        beschreibung: formValues.beschreibung ?? '',
        kategorie: 'Eigene Aufgabe',
        typ: 'Individuell',
        faelligkeit: formValues.faelligkeit,
      });
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-400">
      <div className="flex flex-col gap-200">
        <Header3>Objekte auswählen</Header3>
        <Select
          className="w-full"
          displayOptionsFilter={true}
          onValueChange={objektId => setFormValues({ ...formValues, objektId })}
          options={objekteProvider}
          placeholder={() => <Placeholder icon={House} label="Objekt auswählen" />}
          optionsFilterPlaceholder={`Mindestens ${MIN_CHARS_TO_SEARCH} Zeichen zum Suchen eingeben...`}
          value={formValues.objektId}
        />
      </div>

      <div className="flex flex-col gap-200">
        <Header3>Aufgaben definieren</Header3>

        <Label label="Aufgabentitel" labelPosition="top">
          <InputText
            value={formValues.titel}
            onValueChange={titel => setFormValues({ ...formValues, titel })}
            placeholder="Bitte hier einen aussagekräftigen Titel wählen"
          />
        </Label>

        <Label label="Fällig am" labelPosition="top">
          <InputDate
            value={formValues.faelligkeit}
            onValueChange={faelligkeit => setFormValues({ ...formValues, faelligkeit })}
          />
        </Label>

        <Label label="Beschreibung" labelPosition="top">
          <Textarea
            rows={4}
            value={formValues.beschreibung}
            onValueChange={beschreibung => setFormValues({ ...formValues, beschreibung })}
            placeholder="Bitte beschreibe hier was die Aufgabe in dem ausgewählten Objekt ist"
          />
        </Label>
      </div>

      <div className="flex justify-center">
        <ButtonCtaPrimary type="submit" disabled={!canSubmit} icon={submitting ? LoadingSpinner : Play}>Aufgabe erstellen</ButtonCtaPrimary>
      </div>
    </form>
  );
};

type PlaceholderProps = {
  icon: typeof House;
  label: string;
};

const Placeholder = ({ icon: Icon, label }: PlaceholderProps) => (
  <div className="flex items-center">
    <Icon weight="regular" size={22} className="mr-1" />
    <span>{label}</span>
  </div>
);
